import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PageHeader } from '@/components/admin/AdminShell';
import { MetaBox } from '@/components/admin/MetaBox';
import { Notice } from '@/components/admin/Notice';
import { ErrorState, ForbiddenState, SkeletonRows } from '@/components/admin/states';
import { fetchEvent } from '@/features/events/api';
import { surveyKindLabel } from '@/features/events/eventSurveyTabs';
import {
  ATTITUDE_LABELS,
  fetchEligibleMembers,
  fetchSubcommitteeFeedback,
  submitSubcommitteeFeedback,
  type AttitudeFlag,
} from '@/features/evaluation/api';
import { answerText, questionChoices, questionLabels } from '@/features/surveys/answerFormat';
import {
  fetchEventSurveys,
  fetchSurveyResponses,
  type EventSurvey,
  type EventSurveyKind,
  type SurveyResponse,
} from '@/features/surveys/api';
import { ApiError } from '@/lib/api/http';

/**
 * 優秀者選定・態度フィードバック（理事・講師のみ / implementation-spec §6.2 / DECISIONS D-034・D-281）。
 * イベント詳細は9タブ固定（指示書 §7-2）のため、10個目のタブにせず独立画面とする。
 * 担当会員かつそのイベントの対象者以外を選ぶとAPI側が404を返す（§0-6: 認可はAPI側）。
 * 分科会では事後アンケート・アウトプットを同じ画面で読んでから入力する（D-281）。
 */
export function EventEvaluationPage(): ReactElement {
  const { eventId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const eventQuery = useQuery({
    queryKey: ['event', eventId],
    queryFn: () => fetchEvent(eventId),
    enabled: Boolean(eventId),
  });
  const feedbackQuery = useQuery({
    queryKey: ['subcommittee-feedback', eventId],
    queryFn: () => fetchSubcommitteeFeedback(eventId),
    enabled: Boolean(eventId),
  });
  // 全会員ではなく「担当かつ対象者」だけを取る。全会員だと選んでも保存できない
  // 組み合わせが混ざる（システムテストで発見 / D-047）
  const membersQuery = useQuery({
    queryKey: ['evaluation-eligible-members', eventId],
    queryFn: () => fetchEligibleMembers(eventId),
    enabled: Boolean(eventId),
  });
  const surveysQuery = useQuery({
    queryKey: ['event-surveys', eventId],
    queryFn: () => fetchEventSurveys(eventId),
    enabled: Boolean(eventId),
  });

  const shareSurveys = useMemo(() => {
    const rows = surveysQuery.data ?? [];
    return rows.filter((s) => s.template_kind === 'post' || s.template_kind === 'post_assignment');
  }, [surveysQuery.data]);

  const responseQueries = useQueries({
    queries: shareSurveys.map((survey) => ({
      queryKey: ['survey-responses', survey.id],
      queryFn: () => fetchSurveyResponses(survey.id),
      enabled: Boolean(survey.id) && survey.can_view_results,
    })),
  });

  const [memberId, setMemberId] = useState('');
  const [isBestPerformer, setIsBestPerformer] = useState(false);
  const [attitude, setAttitude] = useState<AttitudeFlag | ''>('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  if (eventQuery.isPending) return <SkeletonRows rows={4} />;
  if (eventQuery.isError) {
    return eventQuery.error instanceof ApiError && eventQuery.error.status === 403 ? (
      <ForbiddenState />
    ) : (
      <ErrorState message="イベントが見つかりません。" />
    );
  }
  if (feedbackQuery.isError && feedbackQuery.error instanceof ApiError && feedbackQuery.error.status === 403) {
    return <ForbiddenState message="理事・講師のみ入力できます。" />;
  }

  const event = eventQuery.data;

  async function onSubmit(): Promise<void> {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await submitSubcommitteeFeedback(eventId, {
        member_id: memberId,
        is_best_performer: isBestPerformer,
        attitude: attitude || null,
      });
      setMemberId('');
      setIsBestPerformer(false);
      setAttitude('');
      setNotice('入力しました。');
      await queryClient.invalidateQueries({ queryKey: ['subcommittee-feedback', eventId] });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '入力に失敗しました。');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <PageHeader title="優秀者選定・態度フィードバック" description={event.title} />

      {error && (
        <Notice kind="error" dismissible={false}>
          {error}
        </Notice>
      )}
      {notice && (
        <Notice kind="success" onDismiss={() => setNotice(null)}>
          {notice}
        </Notice>
      )}

      <MetaBox title="共有資料（事後アンケート・アウトプット）">
        <p className="mb-3 text-sm text-wptext-muted">
          メッセンジャーでの共有の代わりに、対象受講生の回答をここで確認してから選定してください。
        </p>
        {surveysQuery.isPending ? (
          <p className="text-wptext-muted">読み込み中…</p>
        ) : shareSurveys.length === 0 ? (
          <p className="text-wptext-muted">事後アンケート・アウトプットの割当がありません。</p>
        ) : (
          <div className="flex flex-col gap-4">
            {shareSurveys.map((survey, index) => (
              <SharedSurveyBlock
                key={survey.id}
                survey={survey}
                typeCode={event.type_code}
                responses={responseQueries[index]?.data}
                loading={responseQueries[index]?.isPending ?? false}
                error={responseQueries[index]?.isError ?? false}
                focusMemberId={memberId || null}
              />
            ))}
          </div>
        )}
      </MetaBox>

      <MetaBox title="入力">
        <label className="mb-3 block max-w-md">
          <span className="mb-1.5 block text-wptext-muted">対象会員（担当かつこのイベントの対象者のみ）</span>
          <select
            required
            value={memberId}
            onChange={(e) => setMemberId(e.target.value)}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">選択してください</option>
            {(membersQuery.data ?? []).map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}（{m.member_id}）
              </option>
            ))}
          </select>
        </label>

        <label className="mb-3 flex items-center gap-2">
          <input
            type="checkbox"
            checked={isBestPerformer}
            onChange={(e) => setIsBestPerformer(e.target.checked)}
          />
          <span>優秀者に選定する（1イベントあたり上限あり）</span>
        </label>

        <label className="mb-3 block max-w-xs">
          <span className="mb-1.5 block text-wptext-muted">態度フィードバック</span>
          <select
            value={attitude}
            onChange={(e) => setAttitude(e.target.value as AttitudeFlag | '')}
            className="w-full rounded-sm border border-wpborder px-3 py-2"
          >
            <option value="">指定しない</option>
            <option value="good">{ATTITUDE_LABELS.good}</option>
            <option value="bad">{ATTITUDE_LABELS.bad}</option>
          </select>
        </label>

        <button
          type="button"
          disabled={busy || !memberId}
          onClick={() => void onSubmit()}
          className="rounded-sm border border-transparent bg-wpaccent px-3.5 py-2 text-white hover:bg-wpaccent-hover disabled:opacity-50"
        >
          保存する
        </button>
      </MetaBox>

      <MetaBox title="入力済み一覧（自分の担当会員分）">
        {feedbackQuery.isPending ? (
          <p className="text-wptext-muted">読み込み中…</p>
        ) : (feedbackQuery.data ?? []).length === 0 ? (
          <p className="text-wptext-muted">まだ入力がありません。</p>
        ) : (
          <ul className="flex flex-col gap-1">
            {(feedbackQuery.data ?? []).map((f) => (
              <li key={f.id} className="border-b border-wpborder py-1">
                {f.member_name ?? f.member_id}
                {f.is_best_performer && (
                  <span className="ml-2 rounded-sm bg-wpaccent/10 px-1.5 py-0.5 text-xs text-wpaccent">
                    優秀者
                  </span>
                )}
                {f.attitude && (
                  <span className="ml-2 text-wptext-muted">{ATTITUDE_LABELS[f.attitude]}</span>
                )}
              </li>
            ))}
          </ul>
        )}
      </MetaBox>

      <button type="button" onClick={() => navigate(-1)} className="mt-4 text-wpaccent hover:underline">
        ← イベントに戻る
      </button>
    </>
  );
}

function SharedSurveyBlock({
  survey,
  typeCode,
  responses,
  loading,
  error,
  focusMemberId,
}: {
  survey: EventSurvey;
  typeCode: string;
  responses: SurveyResponse[] | undefined;
  loading: boolean;
  error: boolean;
  focusMemberId: string | null;
}): ReactElement {
  const kindLabel = surveyKindLabel(typeCode, survey.template_kind as EventSurveyKind);
  const labels = questionLabels(survey.questions);
  const choices = questionChoices(survey.questions);

  if (!survey.can_view_results) {
    return (
      <div>
        <p className="font-semibold text-wptext-strong">
          {kindLabel}：{survey.template_name}
        </p>
        <p className="text-sm text-wptext-muted">この割当の結果は閲覧できません。</p>
      </div>
    );
  }

  const rows = responses ?? [];
  const focused = focusMemberId ? rows.filter((r) => r.member_id === focusMemberId) : rows;
  const shown = focused.length > 0 ? focused : rows;

  return (
    <div>
      <p className="mb-2 font-semibold text-wptext-strong">
        {kindLabel}：{survey.template_name}
        {focusMemberId && focused.length > 0 && (
          <span className="ml-2 text-sm font-normal text-wptext-muted">（選択中の受講生）</span>
        )}
      </p>
      {loading ? (
        <p className="text-wptext-muted">読み込み中…</p>
      ) : error ? (
        <p className="text-wpnotice-error">回答の読み込みに失敗しました。</p>
      ) : shown.length === 0 ? (
        <p className="text-wptext-muted">まだ提出がありません。</p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shown.map((response) => (
            <li key={response.id} className="rounded-sm border border-wpborder bg-white p-3">
              <p className="mb-1 font-semibold text-wptext-strong">
                {response.member_name ?? response.member_id ?? '匿名'}
                {response.is_overdue && (
                  <span className="ml-2 text-sm font-normal text-wpnotice-error">期限超過</span>
                )}
              </p>
              <ul className="flex flex-col gap-1 text-sm text-wptext-muted">
                {response.answers.map((a) => (
                  <li key={a.question_id}>
                    <span className="text-wptext-strong">{labels[a.question_id] ?? a.question_id}</span>
                    {'：'}
                    {answerText(a.value, choices[a.question_id])}
                  </li>
                ))}
                {response.answers.length === 0 && <li>回答なし</li>}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
